import { Link } from 'react-router-dom'
import type { AdminReport, ReportCatalog, ReportCondition, ReportDefinition, ReportFilter, ReportUserSet } from '../lib/api/adminReports'

const operatorLabels: Record<string, string> = {
  eq: 'is', neq: 'is not', in: 'is one of', gte: 'at least / on or after', lte: 'at most / on or before',
  lastDays: 'within the last', isNull: 'is missing', notNull: 'has a value'
}

type Context = { catalog: ReportCatalog | null; saved: AdminReport[] }

const Filter = ({ filter, dataset, catalog }: { filter: ReportFilter; dataset: string } & Pick<Context, 'catalog'>) => <span>
  {catalog?.datasets[dataset]?.find(field => field.id === filter.field)?.label ?? filter.field} · {operatorLabels[filter.operator] ?? filter.operator}{' '}
  {filter.operator === 'in' ? filter.values?.join(' or ') : filter.value ?? ''}{filter.operator === 'lastDays' ? ' days' : ''}
</span>

const Condition = ({ condition, dataset, catalog }: { condition: ReportCondition; dataset: string } & Pick<Context, 'catalog'>) =>
  condition.kind === 'filter' && condition.filter
    ? <Filter filter={condition.filter} dataset={dataset} catalog={catalog} />
    : <div>{condition.kind === 'all' ? 'All of these conditions' : condition.kind === 'any' ? 'Any of these conditions' : 'Exclude matching records'}
      <ul className="ml-4 mt-1 list-disc space-y-1">{condition.conditions?.map((child, index) => <li key={index}>
        <Condition condition={child} dataset={dataset} catalog={catalog} />
      </li>)}</ul>
    </div>

const Filters = ({ dataset, filters, where, catalog }: {
  dataset: string; filters: ReportFilter[]; where?: ReportCondition | null
} & Pick<Context, 'catalog'>) => <>
  {filters.length === 0 && !where && <p>All records in this search.</p>}
  <ul className="mt-1 space-y-1">{filters.map((filter, index) => <li key={index}>
    <Filter filter={filter} dataset={dataset} catalog={catalog} />
  </li>)}{where && <li><Condition condition={where} dataset={dataset} catalog={catalog} /></li>}</ul>
</>

const UserSet = ({ set, catalog, saved }: { set: ReportUserSet } & Context) => {
  if (set.operation === 'saved') return <Link to={`/reports/${encodeURIComponent(set.reportId ?? '')}`} className="underline">
    Saved search: {saved.find(report => report.id === set.reportId)?.definition.title ?? set.reportId}
  </Link>
  if (set.operation === 'search') return <div>
    <p className="font-medium">Users with matching {set.dataset}</p>
    <Filters dataset={set.dataset ?? ''} filters={set.filters ?? []} where={set.where} catalog={catalog} />
  </div>
  return <div>
    <p className="font-medium">{set.operation === 'union' ? 'Union — users in any search' : set.operation === 'intersection'
      ? 'Intersection — users in every search' : 'Exclusion — first search minus second search'}</p>
    <ol className="ml-4 mt-2 list-decimal space-y-2">{set.operands?.map((child, index) => <li key={index}>
      <UserSet set={child} catalog={catalog} saved={saved} />
    </li>)}</ol>
  </div>
}

export const ReportSearchSummary = ({ definition, catalog, saved }: { definition: ReportDefinition } & Context) => <>
  {definition.userSet && <div className="mb-3" aria-label="Combined user search">
    <UserSet set={definition.userSet} catalog={catalog} saved={saved} />
    <p className="mt-2 text-xs text-muted-foreground">Uses every matching user from each search. Saved searches run again against current data.</p>
  </div>}
  <p className="font-semibold">{definition.userSet ? 'Additional user filters (all must match)' : 'Applied filters (all must match)'}</p>
  <Filters dataset={definition.dataset} filters={definition.filters} where={definition.where} catalog={catalog} />
</>
